import {
    calculateScoreForFields,
    presetForSectionPercent,
} from './auditFormScoring.js';
import { isAuditFieldNotApplicable } from './auditFieldApplicability.js';
import { isRangeAuditGap } from './auditObservationEvidence.js';
import {
    forEachFieldInSection,
    getAllFieldsForSection,
    getSectionMode,
} from './formJsonSections.js';
import { translateWithFallback } from '../i18n/translationUtils.js';

export const AUDIT_GAP_PRIORITY_VALUES = [
    'LOW',
    'MEDIUM',
    'HIGH',
    'LEGISLATED',
];

const PRIORITY_RANK = {
    LEGISLATED: 0,
    HIGH: 1,
    MEDIUM: 2,
    LOW: 3,
};

export function isValidAuditGapPriority(value) {
    const normalized = String(value || '')
        .trim()
        .toUpperCase();
    return AUDIT_GAP_PRIORITY_VALUES.includes(normalized);
}

function normalizePriority(rawPriority) {
    const normalized = String(rawPriority || '')
        .trim()
        .toUpperCase();
    if (Object.prototype.hasOwnProperty.call(PRIORITY_RANK, normalized)) {
        return normalized;
    }
    return 'LOW';
}

function priorityLabel(priority, translateFn = null) {
    if (priority === 'LEGISLATED') {
        return translateWithFallback(
            translateFn,
            'actionItems.shared.priority.legislated',
            'Legislated'
        );
    }
    return priority;
}

function sectionFallback(index, translateFn = null) {
    return translateWithFallback(
        translateFn,
        'audits.panels.overview.defaults.section',
        'Section {number}',
        { number: index + 1 }
    );
}

function subsectionFallback(index, translateFn = null) {
    return translateWithFallback(
        translateFn,
        'audits.panels.overview.defaults.subsection',
        'Subsection {number}',
        { number: index + 1 }
    );
}

function noScoredItemsLabel(translateFn = null) {
    return translateWithFallback(
        translateFn,
        'audits.panels.overview.sectionScores.noScoredItems',
        'No scored items in this section'
    );
}

function pointsDescription(totalScore, totalMaxPossible, translateFn = null) {
    return translateWithFallback(
        translateFn,
        'audits.panels.overview.sectionScores.points',
        '{score} / {max} pts',
        { score: totalScore, max: totalMaxPossible }
    );
}

function unresolvedDescription(count, translateFn = null) {
    return translateWithFallback(
        translateFn,
        'audits.panels.overview.gaps.unresolvedCount',
        '{count} unresolved',
        { count }
    );
}

function resolveCorrectness(field) {
    const options = Array.isArray(field?.params?.options)
        ? field.params.options
        : [];
    const selectedValue = field?.value != null ? String(field.value) : '';
    const selectedOption = options.find(
        (option) => String(option?.value) === selectedValue
    );
    if (selectedOption && typeof selectedOption.isCorrect === 'boolean') {
        return selectedOption.isCorrect;
    }
    return undefined;
}

export function buildGapRowsFromFormJson(formJson, translateFn = null) {
    if (!Array.isArray(formJson)) return [];

    const rows = [];

    formJson.forEach((section, sectionIndex) => {
        forEachFieldInSection(section, ({ field, subIndex, fieldIndex }) => {
            if (field.type === 'h3') return;
            if (isAuditFieldNotApplicable(field)) return;

            const maxScore = Number(field.max_score);
            const isScored = Number.isFinite(maxScore) && maxScore > 0;
            const correct = resolveCorrectness(field);
            const recommendation =
                field?.recommendation != null
                    ? String(field.recommendation).trim()
                    : '';

            const isChoiceGap = isScored && correct === false && recommendation;
            const isRangeGap = isRangeAuditGap(field);

            if (!(isChoiceGap || isRangeGap)) {
                return;
            }

            const priority = normalizePriority(field?.priority);
            const observation =
                field?.observation != null
                    ? String(field.observation).trim()
                    : '';

            rows.push({
                id: `${sectionIndex}-${subIndex}-${fieldIndex}`,
                priority,
                priorityLabel: priorityLabel(priority, translateFn),
                gap: observation || '—',
                recommendation,
            });
        });
    });

    return rows.sort(
        (left, right) =>
            PRIORITY_RANK[left.priority] - PRIORITY_RANK[right.priority]
    );
}

/** @returns {{ subsectionScores: Array<Record<string, unknown>>, hasSubsectionBreakdown: boolean }} */
function subsectionBreakdownFromSection(section, translateFn = null) {
    if (getSectionMode(section) !== 'subsections') {
        return { subsectionScores: [], hasSubsectionBreakdown: false };
    }

    const subs = Array.isArray(section.subsections) ? section.subsections : [];

    const subsectionScores = subs.map((sub, subIndex) => {
        const subHeading =
            String(sub?.heading || '').trim() ||
            subsectionFallback(subIndex, translateFn);
        const subFields = Array.isArray(sub?.fields) ? sub.fields : [];
        const { totalScore, totalMaxPossible } =
            calculateScoreForFields(subFields);

        if (totalMaxPossible <= 0) {
            return {
                id: `${subIndex}-${subHeading}`,
                title: subHeading,
                value: '—',
                description: noScoredItemsLabel(translateFn),
                preset: 'info',
                showProgressCircle: false,
            };
        }

        const percent = Math.round((totalScore / totalMaxPossible) * 100);

        return {
            id: `${subIndex}-${subHeading}`,
            title: subHeading,
            value: percent,
            description: pointsDescription(
                totalScore,
                totalMaxPossible,
                translateFn
            ),
            preset: presetForSectionPercent(percent),
            showProgressCircle: true,
        };
    });

    return {
        subsectionScores,
        hasSubsectionBreakdown: subs.length >= 2,
    };
}

/**
 * One card per top-level section from `form_json`; aggregates subsection fields into the section score.
 * Sets `subsectionScores` and `hasSubsectionBreakdown` when applicable.
 */
export function buildSectionScoreCardsFromFormJson(
    formJson,
    translateFn = null
) {
    if (!Array.isArray(formJson)) return [];

    return formJson
        .map((section, index) => {
            const heading = String(section?.heading || '').trim();
            const title = heading || sectionFallback(index, translateFn);
            const fields = getAllFieldsForSection(section);
            const { totalScore, totalMaxPossible } =
                calculateScoreForFields(fields);
            const { subsectionScores, hasSubsectionBreakdown } =
                subsectionBreakdownFromSection(section, translateFn);

            if (totalMaxPossible <= 0) {
                return {
                    id: `${index}-${title}`,
                    title,
                    value: '—',
                    description: noScoredItemsLabel(translateFn),
                    preset: 'info',
                    showProgressCircle: false,
                    subsectionScores,
                    hasSubsectionBreakdown,
                };
            }

            const percent = Math.round((totalScore / totalMaxPossible) * 100);

            return {
                id: `${index}-${title}`,
                title,
                value: percent,
                description: pointsDescription(
                    totalScore,
                    totalMaxPossible,
                    translateFn
                ),
                preset: presetForSectionPercent(percent),
                showProgressCircle: true,
                subsectionScores,
                hasSubsectionBreakdown,
            };
        })
        .filter((card) => Boolean(card.title));
}

/**
 * Map a persisted gap row to a flat index in `getAllFieldsForSection(section)`, when possible.
 */
function resolveGapFlatFieldIndex(section, gapRow) {
    const fields = getAllFieldsForSection(section);

    const fid = gapRow?.field_id != null ? String(gapRow.field_id).trim() : '';
    if (fid && Array.isArray(fields)) {
        const idx = fields.findIndex((f) => fid === String(f?.id));
        if (idx >= 0) return idx;
    }

    const sk = String(gapRow?.source_key || '');
    if (!sk) return null;

    const idxPart = /^(\d+):idx-(\d+)$/.exec(sk);
    if (idxPart) {
        const n = Number(idxPart[2]);
        return Number.isFinite(n) ? n : null;
    }

    const legacy = sk.split(':');
    if (legacy.length >= 2) {
        const tail = legacy.slice(1).join(':');
        if (/^idx-\d+$/.test(tail)) {
            const n = Number(tail.slice(4));
            return Number.isFinite(n) ? n : null;
        }
        if (Array.isArray(fields)) {
            const idx2 = fields.findIndex((f) => tail === String(f?.id));
            if (idx2 >= 0) return idx2;
        }
    }

    return null;
}

/** Subsection ordinal for a flattened field index, or null if out of range. */
function subsectionIndexForFlatField(section, flatIndex) {
    if (
        flatIndex == null ||
        !Number.isFinite(flatIndex) ||
        flatIndex < 0 ||
        !Number.isInteger(flatIndex) ||
        getSectionMode(section) !== 'subsections'
    ) {
        return null;
    }

    const subs = Array.isArray(section?.subsections) ? section.subsections : [];

    let offset = 0;
    for (let si = 0; si < subs.length; si += 1) {
        const chunk = Array.isArray(subs[si]?.fields)
            ? subs[si].fields.length
            : 0;
        if (flatIndex < offset + chunk) return si;
        offset += chunk;
    }

    return null;
}

/**
 * Aggregate gap totals per subsection (`field_id` / `source_key` → form field mapping).
 */
function subsectionGapBreakdownFromSection(
    section,
    countedGapRowsForSection,
    translateFn = null
) {
    if (
        getSectionMode(section) !== 'subsections' ||
        !Array.isArray(section.subsections) ||
        section.subsections.length < 2
    ) {
        return {
            subsectionGaps: [],
            hasSubsectionGapBreakdown: false,
        };
    }

    const subs = section.subsections;
    const unresolvedStatuses = new Set(['OPEN', 'ACTIONED']);

    /** @type {{ total: number, unresolved: number }[]} */
    const tallies = subs.map(() => ({ total: 0, unresolved: 0 }));

    let unmappedTotal = 0;
    let unmappedUnresolved = 0;

    countedGapRowsForSection.forEach((row) => {
        const flat = resolveGapFlatFieldIndex(section, row);
        const si = subsectionIndexForFlatField(section, flat);

        const isUnresolved = unresolvedStatuses.has(
            String(row?.status || '')
                .trim()
                .toUpperCase()
        );

        if (si == null) {
            unmappedTotal += 1;
            if (isUnresolved) unmappedUnresolved += 1;
            return;
        }

        tallies[si].total += 1;
        if (isUnresolved) tallies[si].unresolved += 1;
    });

    /** @type {Array<Record<string, unknown>>} */
    const subsectionGaps = subs.map((sub, subIndex) => {
        const heading =
            String(sub?.heading || '').trim() ||
            subsectionFallback(subIndex, translateFn);
        const { total, unresolved } = tallies[subIndex];
        return {
            id: `${subIndex}-${heading}-gaps`,
            title: heading,
            value: total,
            description: unresolvedDescription(unresolved, translateFn),
            preset: unresolved > 0 ? 'warning' : 'success',
            showProgressCircle: false,
        };
    });

    if (unmappedTotal > 0) {
        subsectionGaps.push({
            id: '_unmapped-section-gaps',
            title: translateWithFallback(
                translateFn,
                'audits.panels.overview.gaps.otherInSection',
                'Other gaps in section'
            ),
            value: unmappedTotal,
            description: unresolvedDescription(unmappedUnresolved, translateFn),
            preset: unmappedUnresolved > 0 ? 'warning' : 'success',
            showProgressCircle: false,
        });
    }

    return {
        subsectionGaps,
        hasSubsectionGapBreakdown: true,
    };
}

export function buildSectionGapCountCards(
    formJson,
    gapRows,
    translateFn = null
) {
    if (!Array.isArray(formJson)) return [];

    const rows = Array.isArray(gapRows) ? gapRows : [];
    const unresolvedStatuses = new Set(['OPEN', 'ACTIONED']);

    return formJson
        .map((section, index) => {
            const heading = String(section?.heading || '').trim();
            const title = heading || sectionFallback(index, translateFn);
            const sectionRows = rows.filter(
                (row) => Number(row?.section_index) === index
            );
            const countedRows = sectionRows.filter(
                (row) =>
                    String(row?.status || '')
                        .trim()
                        .toUpperCase() !== 'INVALIDATED'
            );
            const total = countedRows.length;
            const unresolved = countedRows.filter((row) =>
                unresolvedStatuses.has(
                    String(row?.status || '')
                        .trim()
                        .toUpperCase()
                )
            ).length;

            const { subsectionGaps, hasSubsectionGapBreakdown } =
                subsectionGapBreakdownFromSection(
                    section,
                    countedRows,
                    translateFn
                );

            return {
                id: `${index}-${title}`,
                title: translateWithFallback(
                    translateFn,
                    'audits.panels.overview.gaps.sectionTitle',
                    '{section} Gaps',
                    { section: title }
                ),
                value: total,
                description: unresolvedDescription(unresolved, translateFn),
                preset: unresolved > 0 ? 'warning' : 'success',
                showProgressCircle: false,
                subsectionGaps,
                hasSubsectionGapBreakdown,
            };
        })
        .filter((card) => Boolean(card.title));
}
